if (!customElements.get('vr-campaign')) {
  customElements.define('vr-campaign', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.viewportObserver?.disconnect();
      this.introObserver?.disconnect();
      this.abort = new AbortController();
      this.index = 0;
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.mobilePaused = false;
      this.mobile = matchMedia('(max-width: 768px), (max-width: 1024px) and (max-height: 600px) and (pointer: coarse)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.slides = [...this.querySelectorAll('[data-campaign-slide]')];
      this.dots = [...this.querySelectorAll('[data-campaign-go]')];
      this.video = this.querySelector('[data-campaign-video]');
      // Keep theme.js pauseVideo() from pausing this banner (and vice versa with the client videos).
      if (this.video) this.video.dataset.autoplay = 'true';
      this.cover = this.querySelector('[data-mobile-cover]');
      this.teaser = this.querySelector('[data-mobile-teaser]');
      this.pauseButton = this.querySelector('[data-campaign-pause]');
      this.mobileButton = this.querySelector('[data-mobile-pause]');
      const on = (target, name, fn) => target?.addEventListener(name, fn, { signal: this.abort.signal });
      const resizeMobile = (event) => {
        // Mobile browser toolbars and the keyboard fire resize while scrolling/typing;
        // only recalculate the banner height when the width really changes (rotation).
        if (event?.type === 'resize' && this.sizedWidth === window.innerWidth) return;
        cancelAnimationFrame(this.viewportFrame);
        this.viewportFrame = requestAnimationFrame(() => this.sizeMobile());
      };
      on(window, 'resize', resizeMobile);
      on(window.visualViewport, 'resize', resizeMobile);
      this.viewportObserver = new ResizeObserver(resizeMobile);
      document.querySelectorAll('.header-wrapper, .shopify-section-group-header-group').forEach(header => this.viewportObserver.observe(header));
      document.fonts?.ready.then(() => { if (this.isConnected) resizeMobile(); });
      on(this.querySelector('[data-campaign-previous]'), 'click', () => this.show(this.index - 1));
      on(this.querySelector('[data-campaign-next]'), 'click', () => this.show(this.index + 1));
      this.dots.forEach((dot, i) => on(dot, 'click', () => this.show(i)));
      on(this.pauseButton, 'click', () => { this.paused = !this.paused; this.updateControls(); this.scheduleDesktop(); });
      on(this, 'mouseenter', () => { this.hovered = true; this.scheduleDesktop(); });
      on(this, 'mouseleave', () => { this.hovered = false; this.scheduleDesktop(); });
      on(this, 'focusin', () => { this.focused = true; this.scheduleDesktop(); if (this.mobileStage === 'cover') clearTimeout(this.mobileTimer); });
      on(this, 'focusout', (event) => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.scheduleDesktop(); if (this.mobile.matches && this.mobileStage === 'cover') this.resumeMobile(); } });
      on(this.video, 'ended', () => this.showTeaser());
      on(this.video, 'error', () => this.videoUnavailable());
      on(this.mobileButton, 'click', () => {
        this.mobilePaused = !this.mobilePaused;
        this.userStarted = true;
        clearTimeout(this.mobileTimer);
        if (this.mobilePaused) this.video?.pause();
        else this.resumeMobile();
        this.updateControls();
      });
      on(document, 'visibilitychange', () => {
        this.scheduleDesktop();
        if (document.hidden) { clearTimeout(this.mobileTimer); this.video?.pause(); }
        else if (this.mobile.matches && !this.mobilePaused) {
          this.resumeMobile();
        }
      });
      on(this.mobile, 'change', () => this.setupMode());
      on(this.reduced, 'change', () => { this.userStarted = false; this.setupMode(); });
      on(document, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (index >= 0) { this.show(index); this.paused = true; this.updateControls(); this.scheduleDesktop(); }
      });
      const intro = document.getElementById('vr-editorial-intro');
      this.waitingIntro = Boolean(intro);
      if (intro) {
        this.introObserver = new MutationObserver(() => {
          if (!intro.isConnected) {
            this.waitingIntro = false;
            this.introObserver.disconnect();
            this.setupMode();
          }
        });
        this.introObserver.observe(document.body, { childList: true });
      }
      this.setupMode();
    }
    disconnectedCallback() {
      clearTimeout(this.desktopTimer);
      clearTimeout(this.mobileTimer);
      this.video?.pause();
      this.abort?.abort();
      this.viewportObserver?.disconnect();
      this.introObserver?.disconnect();
      cancelAnimationFrame(this.viewportFrame);
    }
    sizeMobile() {
      if (!this.mobile.matches) {
        this.sizedWidth = undefined;
        this.style.removeProperty('--vr-mobile-available-height');
        return;
      }
      const viewport = window.visualViewport;
      const height = viewport && viewport.scale === 1 ? viewport.height : window.innerHeight;
      const offset = Math.max(0, this.getBoundingClientRect().top + window.scrollY);
      this.style.setProperty('--vr-mobile-available-height', `${Math.max(1, Math.round(height - offset))}px`);
      this.sizedWidth = window.innerWidth;
    }
    setupMode() {
      this.sizeMobile();
      clearTimeout(this.desktopTimer);
      clearTimeout(this.mobileTimer);
      this.video?.pause();
      this.paused = this.reduced.matches;
      this.mobilePaused = this.reduced.matches && !this.userStarted;
      if (this.mobile.matches && this.video) {
        if (!this.video.dataset.loaded) {
          this.video.querySelectorAll('[data-mobile-src]').forEach(source => { source.src = source.dataset.mobileSrc; });
          this.video.muted = true;
          this.video.loop = false;
          this.video.load();
          this.video.dataset.loaded = 'true';
        }
        if (this.teaser) this.showTeaser();
        else this.showCover();
      } else this.scheduleDesktop();
      this.updateControls();
    }
    show(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
      this.dots.forEach((dot, i) => { if (i === this.index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
      this.scheduleDesktop();
    }
    scheduleDesktop() {
      clearTimeout(this.desktopTimer);
      if (this.waitingIntro || this.mobile?.matches || this.paused || this.hovered || this.focused || document.hidden || this.slides.length < 2) return;
      this.desktopTimer = setTimeout(() => this.show(this.index + 1), Number(this.slides[this.index]?.dataset.slideDelay) || Number(this.dataset.delay) || 5000);
    }
    showCover() {
      clearTimeout(this.mobileTimer);
      if (!this.video) return;
      if (!this.cover) {
        if (this.teaser) this.teaser.hidden = true;
        this.video.hidden = false;
        this.mobileStage = 'video';
        this.playVideo(true);
        return;
      }
      this.video.pause();
      this.video.hidden = true;
      this.mobileStage = 'cover';
      if (this.teaser) this.teaser.hidden = true;
      if (this.cover) this.cover.hidden = false;
      if (this.mobile.matches && !this.waitingIntro && !this.mobilePaused && !this.focused && !document.hidden) {
        const scheduleVideo = () => {
          if (!this.isConnected || !this.mobile.matches || this.mobilePaused || document.hidden || this.mobileStage !== 'cover') return;
          clearTimeout(this.mobileTimer);
          this.mobileTimer = setTimeout(() => this.playVideo(true), Number(this.cover.dataset.coverDelay) || 3000);
        };
        const coverImage = this.cover?.querySelector('img');
        if (!coverImage || (coverImage.complete && coverImage.naturalWidth > 0)) scheduleVideo();
        else {
          coverImage.addEventListener('load', scheduleVideo, { once: true, signal: this.abort.signal });
          coverImage.addEventListener('error', scheduleVideo, { once: true, signal: this.abort.signal });
        }
      }
    }
    showTeaser() {
      if (!this.teaser) { this.showCover(); return; }
      clearTimeout(this.mobileTimer);
      this.video?.pause();
      if (this.video) this.video.hidden = true;
      if (this.cover) this.cover.hidden = true;
      this.teaser.hidden = false;
      this.mobileStage = 'teaser';
      if (this.mobile.matches && !this.waitingIntro && !this.mobilePaused && !this.focused && !document.hidden) {
        this.mobileTimer = setTimeout(() => this.showCover(), 4000);
      }
    }
    resumeMobile() {
      if (this.mobileStage === 'teaser') this.showTeaser();
      else if (this.video?.hidden) this.showCover();
      else this.playVideo();
    }
    async playVideo(restart = false) {
      if (!this.video || !this.mobile.matches || this.mobilePaused || document.hidden) return;
      if (restart || this.video.ended) this.video.currentTime = 0;
      try {
        await this.video.play();
        if (!this.isConnected || !this.mobile.matches || this.mobilePaused || document.hidden) { this.video.pause(); return; }
        this.video.hidden = false;
        this.mobileStage = 'video';
        if (this.teaser) this.teaser.hidden = true;
        if (this.cover) this.cover.hidden = true;
        const message = this.querySelector('[data-video-message]');
        if (message) message.hidden = true;
      } catch {
        this.videoUnavailable();
      }
    }
    videoUnavailable() {
      clearTimeout(this.mobileTimer);
      if (this.video) { this.video.pause(); this.video.hidden = Boolean(this.cover); }
      if (this.cover) this.cover.hidden = false;
      if (this.teaser) this.teaser.hidden = true;
      this.mobileStage = this.cover ? 'cover' : 'video';
      this.mobilePaused = true;
      this.updateControls();
    }
    updateControls() {
      if (this.pauseButton) { this.pauseButton.textContent = this.paused ? 'Reanudar' : 'Pausar'; this.pauseButton.setAttribute('aria-pressed', String(this.paused)); }
      if (this.mobileButton) { this.mobileButton.textContent = this.mobilePaused ? 'Reproducir vídeo' : 'Pausar'; this.mobileButton.setAttribute('aria-pressed', String(this.mobilePaused)); }
    }
  });
}
